import { StyleSheet, type TextStyle } from "react-native";

export type FontSize = "xs" | "sm" | "md" | "base" | "lg" | "xl" | "xxl";

export const FontHeights: Record<FontSize, number> = {
    xs: 10,
    sm: 12,
    md: 14,
    base: 16,
    lg: 20,
    xl: 24,
    xxl: 32,
};

export const LineHeights: Record<FontSize, number> = {
    xs: 16,
    sm: 18,
    md: 22,
    base: 24,
    lg: 28,
    xl: 32,
    xxl: 38,
};
type FontHeight<T extends FontSize> = (typeof FontHeights)[T];
type LineHeight<T extends FontSize> = (typeof FontHeights)[T];

export type FontStyle = "italic" | "normal";
type FontDecorationStyle = TextStyle & { fontStyle: FontStyle };

export type FontWeight = "regular" | "medium" | "semibold";
type FontWeightStyle = TextStyle & { fontWeight: number };

type LineHeightStyle<T extends FontSize> = TextStyle & { lineHeight: LineHeight<T> };
type LineHeightRecord<T extends FontSize> = Record<T, LineHeightStyle<T>>;

type FontSizeStyle<T extends FontSize> = TextStyle & { fontSize: FontHeight<T> } & LineHeightStyle<T>;
type FontSizeRecord<T extends FontSize> = Record<T, FontSizeStyle<T>>;

type FontStyleRecord = Record<FontWeight, FontWeightStyle> &
    Record<FontStyle, FontDecorationStyle> &
    FontSizeRecord<FontSize>;

export type TextAlignment = "auto" | "left" | "right" | "center" | "justify";
type TextAlignmentStyle<T extends TextAlignment> = TextStyle & { textAlign: T };
type TextAlignmentRecord<T extends TextAlignment> = Record<T, TextAlignmentStyle<T>>;

export const Leading: LineHeightRecord<FontSize> = StyleSheet.create({
    xs: { lineHeight: LineHeights.xs },
    sm: { lineHeight: LineHeights.sm },
    md: { lineHeight: LineHeights.md },
    base: { lineHeight: LineHeights.base },
    lg: { lineHeight: LineHeights.lg },
    xl: { lineHeight: LineHeights.xl },
    xxl: { lineHeight: LineHeights.xxl },
});

export const Alignment: TextAlignmentRecord<TextAlignment> = StyleSheet.create({
    auto: { textAlign: "auto" },
    left: { textAlign: "left" },
    right: { textAlign: "right" },
    center: { textAlign: "center" },
    justify: { textAlign: "justify" },
});

export const Font: FontStyleRecord = StyleSheet.create({
    normal: {
        fontStyle: "normal",
    },
    italic: {
        fontStyle: "italic",
    },
    regular: {
        fontWeight: 400,
    },
    medium: {
        fontWeight: 500,
    },
    semibold: {
        fontWeight: 600,
    },
    xs: {
        fontSize: FontHeights.xs,
        lineHeight: LineHeights.xs,
    },
    sm: {
        fontSize: FontHeights.sm,
        lineHeight: LineHeights.sm,
    },
    md: {
        fontSize: FontHeights.md,
        lineHeight: LineHeights.md,
    },
    base: {
        fontSize: FontHeights.base,
        lineHeight: LineHeights.base,
    },
    lg: {
        fontSize: FontHeights.lg,
        lineHeight: LineHeights.lg,
    },
    xl: {
        fontSize: FontHeights.xl,
        lineHeight: LineHeights.xl,
    },
    xxl: {
        fontSize: FontHeights.xxl,
        lineHeight: LineHeights.xxl,
    },
});
